import { useCallback } from "react";
import type { AuthUser } from "@mind-sands/shared";
import { isAdminUser } from "@/modules/auth/lib/role";
import { useAuthStore } from "@/modules/auth/store";
import { useSceneStore } from "../stores/sceneStore";
import { useUiStore } from "../stores/uiStore";

export function selectCanEdit(
  user: AuthUser | null,
  spaceKind: "curated" | "user",
  activeSpaceOwnerId: string | null,
): boolean {
  if (spaceKind === "user") {
    return !!user && user.id === activeSpaceOwnerId;
  }
  return isAdminUser(user);
}

/** Resource-scoped edit permission for the active space. */
export function useSpacePermissions() {
  const user = useAuthStore((s) => s.user);
  const spaceKind = useSceneStore((s) => s.spaceKind);
  const activeSpaceOwnerId = useSceneStore((s) => s.activeSpaceOwnerId);

  const canEdit = selectCanEdit(user, spaceKind, activeSpaceOwnerId);

  return { canEdit, spaceKind };
}

/** Read-only edit flag without auth fetch side effects — for hot 3D render paths. */
export function useIsReadonly(): boolean {
  const user = useAuthStore((s) => s.user);
  const spaceKind = useSceneStore((s) => s.spaceKind);
  const activeSpaceOwnerId = useSceneStore((s) => s.activeSpaceOwnerId);
  const editMode = useUiStore((s) => s.editMode);
  const canEdit = selectCanEdit(user, spaceKind, activeSpaceOwnerId);
  return !canEdit || editMode === "readonly";
}

/** Hook that respects space permissions when toggling edit mode. */
export function useCanEditActions() {
  const { canEdit } = useSpacePermissions();

  const guardEdit = useCallback(
    (action: () => void) => {
      if (canEdit) action();
    },
    [canEdit],
  );

  return { canEdit, guardEdit };
}
